import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowDown, ArrowRight, Target, Compass, Sparkles } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { STATS, MANIFESTO, ACTIVITIES, IMAGES, SITE } from "@/data/site";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const yBig = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const ySmall = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <div data-testid="home-page">
      {/* ===================== HERO ===================== */}
      <section ref={heroRef} className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-36 md:pt-44 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 mb-8"
              >
                <span className="w-2 h-2 rounded-full bg-michma-coral" />
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                  {SITE.tagline}
                </span>
              </motion.div>

              <h1 className="font-heading font-extrabold text-5xl sm:text-6xl lg:text-[5.5rem] tracking-tighter leading-[0.9] text-michma-ink">
                <MaskedLines
                  lines={["Empowering", "youth through"]}
                  delay={0.2}
                />
                <span className="reveal-mask">
                  <motion.span
                    className="block text-michma"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.9, delay: 0.4, ease: [0.76, 0, 0.24, 1] }}
                  >
                    health literacy.
                  </motion.span>
                </span>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.7 }}
                className="mt-8 max-w-xl text-lg lg:text-xl leading-relaxed text-slate-500"
              >
                At MICHMA, our vision for global health requires an identity that breaks away from traditional, rigid medical tropes. 
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85, duration: 0.7 }}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <Link
                  to="/activity"
                  data-testid="hero-primary-cta"
                  className="group inline-flex items-center gap-2 bg-michma text-white rounded-full px-8 py-4 font-semibold hover:bg-michma-hover hover:-translate-y-0.5 transition-[transform,background-color] duration-300"
                >
                  Explore our Activity
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <a
                  href={SITE.heliosUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-helios-cta"
                  className="group inline-flex items-center gap-2 border border-michma text-michma rounded-full px-8 py-4 font-semibold hover:bg-michma-tint transition-colors duration-300"
                >
                  Helios
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </motion.div>
            </div>

            {/* Clipped, parallaxing images */}
            <div className="lg:col-span-5 relative h-[340px] sm:h-[440px] lg:h-[520px]">
              <motion.div
                style={{ y: yBig }}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.4, ease: [0.76, 0, 0.24, 1] }}
                className="absolute right-0 top-0 w-[78%] h-[80%] rounded-[999px_999px_24px_24px] overflow-hidden"
              >
                <img
                  src={IMAGES.heroYouth}
                  alt="Young people learning together"
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <motion.div
                style={{ y: ySmall }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.6, ease: [0.76, 0, 0.24, 1] }}
                className="absolute left-0 bottom-0 w-[52%] h-[46%] rounded-3xl overflow-hidden border-4 border-white shadow-2xl"
              >
                <img
                  src={IMAGES.workshop1}
                  alt="Health literacy workshop"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="mt-16 flex items-center gap-2 text-slate-400 font-mono text-xs uppercase tracking-[0.2em]"
          >
            <ArrowDown className="w-4 h-4 animate-bounce" />
            Scroll to discover
          </motion.div>
        </div>
      </section>

      {/* ===================== STATS ===================== 
      <section className="border-y border-slate-100 bg-michma-soft">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div data-testid={`stat-${i}`}>
                <p className="font-heading font-extrabold text-5xl lg:text-6xl tracking-tighter text-michma">
                  {s.value}
                </p>
                <p className="mt-2 text-sm text-slate-500 leading-snug">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>*/}

      {/* ===================== STRATEGIC FRAMEWORK & PHI DOCTRINE ===================== */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        {/* Framework Header */}
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-michma mb-4">
                Strategic Framework
              </p>
              <h2 className="font-heading font-bold text-4xl lg:text-5xl tracking-tight leading-none text-michma-ink max-w-2xl">
                Transforming public health across all life stages.
              </h2>
            </div>
          </div>
        </Reveal>

        {/* 3 Pillar Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20 md:mb-28">
          {/* Vision */}
          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl border border-slate-200 bg-white p-8 md:p-10 flex flex-col justify-between hover:border-michma transition-all duration-300 group shadow-sm hover:shadow-md">
              <div>
                <div className="flex items-center justify-between mb-8">
                  <span className="inline-flex items-center gap-2 rounded-full bg-michma-tint/60 px-3 py-1 font-mono text-xs uppercase tracking-[0.2em] text-michma font-semibold">
                    Vision
                  </span>
                  <Compass className="w-5 h-5 text-michma opacity-60 group-hover:opacity-100 transition-opacity" />
                </div>
                <h3 className="font-heading font-bold text-2xl tracking-tight text-michma-ink mb-4">
                  Continuous, Gatekeeper-Free Rights
                </h3>
                <p className="text-slate-500 leading-relaxed text-base">
                  To lead a global movement where health literacy and ownership are continuous, gatekeeper-free rights, transforming population health into sustainable national capital from womb to tomb.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Mission */}
          <Reveal delay={0.2}>
            <div className="h-full rounded-3xl border border-slate-200 bg-white p-8 md:p-10 flex flex-col justify-between hover:border-michma transition-all duration-300 group shadow-sm hover:shadow-md">
              <div>
                <div className="flex items-center justify-between mb-8">
                  <span className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 font-mono text-xs uppercase tracking-[0.2em] text-michma-coral font-semibold">
                    Mission
                  </span>
                  <Target className="w-5 h-5 text-michma-coral opacity-60 group-hover:opacity-100 transition-opacity" />
                </div>
                <h3 className="font-heading font-bold text-2xl tracking-tight text-michma-ink mb-4">
                  Eliminating Barriers
                </h3>
                <p className="text-slate-500 leading-relaxed text-base">
                  To eliminate institutional barriers in healthcare by leveraging digital innovation, evidence-based research, and community-driven initiatives, delivering friction-free health literacy, robust monitoring, and equitable wellness solutions across all life stages.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Core Purpose */}
          <Reveal delay={0.3}>
            <div className="h-full rounded-3xl border border-slate-200 bg-white p-8 md:p-10 flex flex-col justify-between hover:border-michma transition-all duration-300 group shadow-sm hover:shadow-md">
              <div>
                <div className="flex items-center justify-between mb-8">
                  <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 font-mono text-xs uppercase tracking-[0.2em] text-slate-700 font-semibold">
                    Core Purpose
                  </span>
                  <Sparkles className="w-5 h-5 text-slate-600 opacity-60 group-hover:opacity-100 transition-opacity" />
                </div>
                <h3 className="font-heading font-bold text-2xl tracking-tight text-michma-ink mb-4">
                  A Lifelong Ecosystem
                </h3>
                <p className="text-slate-500 leading-relaxed text-base">
                  To redefine public health from an episodic, sickness-driven intervention into a lifelong, self-sustaining ecosystem where every individual possesses the knowledge, tools, and agency to manage their well-being.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        {/* ===================== PHI DOCTRINE ===================== */}
        <Reveal>
          <div className="rounded-[2.5rem] bg-slate-900 text-white p-8 md:p-16 relative overflow-hidden">
            {/* Background Decorative Gradient Blobs */}
            <div className="absolute top-0 right-0 -translate-y-12 translate-x-12 w-96 h-96 bg-michma/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 translate-y-12 -translate-x-12 w-96 h-96 bg-michma-coral/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 max-w-3xl mb-12 md:mb-16">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.2em] text-michma-coral mb-6">
                The PHI Doctrine
              </span>
              <h3 className="font-heading font-extrabold text-3xl md:text-5xl tracking-tight leading-tight text-white mb-6">
                Womb-to-Tomb Continuity
              </h3>
              <p className="text-slate-300 text-lg md:text-xl leading-relaxed">
                Public Health Infinitism (PHI) views human health not as a series of isolated medical events, but as an unbroken, lifelong continuum.
              </p>
            </div>

            {/* Lifecycle Steps Grid */}
            <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pt-8 border-t border-slate-800">
              {/* Step 1 */}
              <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 hover:border-michma/50 transition-colors">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-michma mb-3 block">
                  Phase 01
                </span>
                <h4 className="font-heading font-bold text-lg text-white mb-3">
                  Prenatal & Maternal Foundations
                </h4>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Establishing early health literacy, maternal guidance, and structural monitoring before birth.
                </p>
              </div>

              {/* Step 2 */}
              <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 hover:border-michma/50 transition-colors">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-michma mb-3 block">
                  Phase 02
                </span>
                <h4 className="font-heading font-bold text-lg text-white mb-3">
                  Developmental Literacy
                </h4>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Embedding core health habits and preventative literacy early through structured education and community outreach.
                </p>
              </div>

              {/* Step 3 */}
              <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 hover:border-michma/50 transition-colors">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-michma mb-3 block">
                  Phase 03
                </span>
                <h4 className="font-heading font-bold text-lg text-white mb-3">
                  Active Ownership
                </h4>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Empowering individuals with continuous monitoring, data-driven self-care, and professional development (such as CMEs for practitioners).
                </p>
              </div>

              {/* Step 4 */}
              <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 hover:border-michma/50 transition-colors">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-michma mb-3 block">
                  Phase 04
                </span>
                <h4 className="font-heading font-bold text-lg text-white mb-3">
                  Dignified Longevity
                </h4>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Maintaining quality of life, cognitive health, and chronic disease management through sustained ecosystem support.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
      {/* ===================== MANIFESTO ===================== */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36 border-t border-slate-100">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-michma mb-4">
            Our Objective
          </p>
          <h2 className="font-heading font-bold text-4xl lg:text-5xl tracking-tight leading-none text-michma-ink max-w-3xl">
            Objectives that shape everything.
          </h2>
        </Reveal>

        <div className="mt-16 md:mt-24 space-y-16 md:space-y-24">
          {MANIFESTO.map((m) => (
            <Reveal key={m.n}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-start border-t border-slate-200 pt-10">
                <div className="md:col-span-3">
                  <span className="chapter-number text-7xl lg:text-8xl">{m.n}</span>
                </div>
                <div className="md:col-span-9 grid md:grid-cols-2 gap-6">
                  <h3 className="font-heading font-bold text-2xl lg:text-3xl tracking-tight text-michma-ink">
                    {m.title}
                  </h3>
                  <p className="text-lg leading-relaxed text-slate-500">{m.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===================== MARQUEE ===================== */}
      <section className="py-8 border-y border-slate-100 bg-white">
        <Marquee speed={38} gradient={false} className="overflow-hidden">
          {Array.from({ length: 3 }).map((_, k) => (
            <span
              key={k}
              className="font-heading font-extrabold text-4xl md:text-6xl tracking-tighter text-michma-ink flex items-center"
            >
              <span className="mx-8">Empowering Education</span>
              <span className="mx-8 text-michma-coral">•</span>
              <span className="mx-8 text-michma">Enhancing Health</span>
              <span className="mx-8 text-michma-coral">•</span>
              <span className="mx-8">Sustainable Finance</span>
              <span className="mx-8 text-michma-coral">•</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* ===================== ACTIVITY PREVIEW ===================== */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-michma mb-4">
              Our activity
            </p>
            <h2 className="font-heading font-bold text-4xl lg:text-5xl tracking-tight leading-none text-michma-ink max-w-2xl">
              Programs that put knowledge into young hands.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/activity"
              data-testid="activity-view-all"
              className="group inline-flex items-center gap-2 font-semibold text-michma"
            >
              View all programs
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {ACTIVITIES.slice(0, 3).map((a, i) => (
            <Reveal key={a.title} delay={i * 0.1}>
              <article
                data-testid={`activity-card-${i}`}
                className="group h-full rounded-3xl border border-slate-200 overflow-hidden bg-white hover:border-michma transition-colors duration-300"
              >
                <div className="overflow-hidden aspect-[4/3]">
                  <img
                    src={a.image}
                    alt={a.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-8">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-michma">
                    {a.tag}
                  </span>
                  <h3 className="mt-3 font-heading font-bold text-2xl tracking-tight text-michma-ink">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-slate-500 leading-relaxed">{a.body}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}